import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Hook to detect and notify users of app updates
 * Checks if service worker has been updated
 */
export function useUpdateNotification() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Check for updates periodically (every 5 minutes to reduce violations)
      const intervalId = setInterval(() => {
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg) {
            reg.update().catch(() => {
              // Silently ignore update errors in production
            });
          }
        }).catch(() => {
          // Silently ignore registration errors
        });
      }, 300000); // 5 minutes instead of 1 minute

      // Listen for service worker updates
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);

        // Check if there's a waiting service worker
        if (reg.waiting) {
          setUpdateAvailable(true);
          showUpdateNotification();
        }

        // Listen for new service worker installing
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker is ready
                setUpdateAvailable(true);
                showUpdateNotification();
              }
            });
          }
        });
      }).catch(() => {
        // Silently handle service worker registration failures
        if (import.meta.env.DEV) {
          console.log('Service worker not available');
        }
      });

      // Listen for controller change (service worker activated)
      let controllerChangeHandled = false;
      const handleControllerChange = () => {
        // Prevent duplicate reloads
        if (controllerChangeHandled) return;
        controllerChangeHandled = true;
        
        // Save current location before reload (for HashRouter)
        const currentHash = window.location.hash;
        if (currentHash && currentHash !== '#/' && currentHash !== '#/login') {
          sessionStorage.setItem('preUpdateRoute', currentHash);
        }
        
        // New service worker has taken control - reload the page
        window.location.reload();
      };
      
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      
      return () => {
        clearInterval(intervalId);
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };

    }
  }, []);

  const showUpdateNotification = () => {
    toast.info('New version available!', {
      description: 'Click to update to the latest version',
      duration: 10000,
      action: {
        label: 'Update',
        onClick: () => {
          handleUpdate();
        },
      },
    });
  };

  const handleUpdate = () => {
    if (registration?.waiting) {
      // Tell the waiting service worker to activate
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  return {
    updateAvailable,
    updateApp: handleUpdate,
  };
}

